<!--
  画面名： 従業員のQRリストを表示する画面
  画面概要： 表示されている情報でPDF出力ができる
  備考：
-->

<template>
  <div class="employeeManagementTable">
    <!-- pdf出力時のプログレスバー -->
    <div v-if="isProgress">
      <v-progress-linear v-model="progressStatus" />
    </div>

    <v-toolbar flat color="white">
      <!-- ページタイトル -->
      <v-toolbar-title>
        <PageTitle :PageTitle="$language.LabelQrList" />
      </v-toolbar-title>
      <v-spacer />
      <!-- 戻るボタン -->
      <v-btn
        @click="back"
        small
        class="btn"
      >
        {{ $language.LabelBack }}
      </v-btn>
      <!-- PDF出力 -->
      <v-btn
        @click="openDialog"
        small
        class="btn"
        :disabled="getTableData.length === 0"
      >
        {{ $language.LabelOutputQr }}
      </v-btn>
    </v-toolbar>
    <v-toolbar flat color="white">
      <v-flex xs3 offset-9 style="margin-left: auto; margin-right: 0;">
      <!-- 検索要素 -->
        <v-text-field
          v-model="search"
          append-icon="search"
          label="このリストを検索..."
          single-line
          hide-details
        />
      </v-flex>
    </v-toolbar>

    <!-- 従業員一覧表要素 -->
    <template>
      <!-- ローディング -->
      <span v-if="isLoading">
      </span>
      <span style="margin-top:2px;display:block;" v-else>
          <v-data-table
            :headers="headers"
            :items="getTableData"
            :pagination.sync="pagination"
            class="white elevation-1"
          >
            <template v-slot:headers="props">
              <th
                v-for="header in props.headers"
                :key="header.text"
                align="left"
                :class="['column sortable', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
              >
                <!-- ヘッダー -->
                <div>
                  {{ header.text }}
                  <span v-if="header.value === 'storeName' || header.value === 'employeeID'">
                    <v-icon @click="changeSort(header.value)" small>arrow_upward</v-icon>
                  </span>
                </div>
              </th>
            </template>

            <template v-slot:items="props" >
              <tr @click="props.item.checked = !props.item.checked; add(props.item.employeeID)" :class="{checkd: props.item.checked}">
                <td v-show="false"><input type="checkbox" :value="props.item.employeeID" v-model="checked"></td>
                <!-- 店舗名 -->
                <td class="noWrap">{{ props.item.storeName }}</td>
                <!-- 従業員ID -->
                <td class="noWrap">{{ props.item.employeeID }}</td>
                <!-- 姓 -->
                <td class="noWrap">{{ props.item.lastName }}</td>
                <!-- 名 -->
                <td class="noWrap">{{ props.item.firstName }}</td>
                <!-- QRコード -->
                <div>
                  <td>
                    <qriously
                      class="capture"
                      :id="props.item.employeeID"
                      :value="getQrValue(props.item)"
                      :size="60"
                      style="padding-top: 3px;"
                    />
                  </td>
                </div>
              </tr>
            </template>
          </v-data-table>
        </span>
    </template>
    <QrDialog
      ref="qrDialog"
      :selected="isChecked"
      @allOutputPdf="allOutputPdf"
      @outputPdf="outputPdf"
    />
    <div v-for="item in employeeData" :key="item.employeeID" style="display:none;">
      <qriously
        class="capture"
        :id="item.employeeID"
        :value="getQrValue(item)"
        :size="60"
        style="padding-top: 3px;"
      />
    </div>
  </div>
</template>

<script>
// ページタイトル
import PageTitle from '@/components/parts/common/PageTitle'
// PDF出力用
import JsPDF from 'jspdf'
// 文字コードエンコード用
import WebKoruri from '@/assets/json/webKoruri.json'
// 定義ファイル取得
import Config from '@/config/managementFunction/employee.json'
// 確認ダイアログ
import QrDialog from '@/components/parts/managementFunction/QrDialog'
import SystemAndManagement from '@/components/pages/common/SystemAndManagement'
// 日付計算用
import moment from 'moment'

export default {
  // 使用コンポーネント
  components: {
    PageTitle,
    QrDialog,
    // eslint-disable-next-line vue/no-unused-components
    SystemAndManagement
  },
  // コンポーネント内の変数を定義
  data () {
    return {
      // ページネーション用の変数
      pagination: {
        sortBy: 'employeeID',
        descending: false,
        rowsPerPage: 5,
        page: 1
      },
      // 検索の値を入れる要素
      search: '',
      // 従業員一覧表ヘッダー
      headers: [
        {
          text: this.$language.LabelStoreName,
          value: 'storeName'
        },
        {
          text: this.$language.LabelEmployeeID,
          value: 'employeeID'
        },
        {
          text: this.$language.LabelFirstName,
          value: 'firstName'
        },
        {
          text: this.$language.LabelLastName,
          value: 'lastName'
        },
        {
          text: this.$language.LabelQr,
          value: 'qrCode'
        }
      ],
      // 事業者ID
      businessCompanyId: null,
      // プログレスバーの判定
      isProgress: false,
      // プログレスバーの進捗
      progressStatus: 0,
      // 進捗の単位
      progressUnit: 0,
      // チェックボックスのチェック内容
      checked: [],
      // 全てチェック
      isAllSelected: false,
      // pdfオブジェクト用変数
      pdf: null,
      // 次ページカウント用変数
      page: 0,
      // pdf出力対象データ
      outputData: [],
      // 横線の高さを保持する変数
      lineY: 0,
      // 従業員情報
      employeeData: [],
      // ローディング判断用
      isLoading: true
    }
  },
  // プロパティを定義
  computed: {
    // テーブルデータ取得処理
    getTableData () {
      // リストデータを作成
      let returnData = this.filter(this.employeeData, this.search)
      // 返却
      return returnData
    },
    // チェックされているか
    isChecked () {
      // チェックされているか
      let isChecked = this.checked.length > 0
      return isChecked
    }
  },
  // 画面表示時処理
  created () {
    // データベース受け取った従業員データの格納
    this.setEmployeeData()
  },
  // 関数群
  methods: {
    // QRコード生成用情報取得（事業者ID,店舗ID,従業員ID）
    getQrValue (item) {
      return item.businessCompanyId + ',' + item.storeID + ',' + item.employeeID
    },
    // チェックボックス追加処理
    add (id) {
      for (let i = 0; i < this.checked.length; i++) {
        if (id === this.checked[i]) {
          this.checked.splice(i, 1)
          return
        }
      }
      this.checked.push(id)
    },
    openDialog () {
      this.$refs.qrDialog.open()
    },
    // 戻る処理
    back () {
      // 従業員一覧へ遷移
      this.$router.push({ name: 'Employee' })
    },
    // pdfの中央に線を引く
    drawACenterLine () {
      this.pdf.line(
        Config.QrList.cLine.X,
        Config.QrList.cLine.Y1,
        Config.QrList.cLine.X,
        Config.QrList.cLine.Y2)
    },
    // PDFの初期設定
    initPdf () {
      // 文字エンコード情報挿入
      let webKoruri = WebKoruri.text
      let options = {
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        putOnlyUsedFonts: true
      }

      // PDF初期化
      let pdf = new JsPDF(options)
      // PDFのフォント設定
      pdf.addFileToVFS('WebKoruri.ttf', webKoruri)
      pdf.addFont('WebKoruri.ttf', 'WebKoruri', 'regular')
      pdf.setFont('WebKoruri', 'regular')
      pdf.setFontSize(16)
      return pdf
    },
    // PDFにQRコードをセット
    setQRImage (pdf, count, lineCount, qrCode) {
      let format = 'JPEG'
      // 高さ調整
      let qrY = Config.QrList.qr.Y + (Config.QrList.lineHeight * lineCount)
      // 偶数番号、奇数番号でX座標を左右に分ける
      if ((count % 2) !== 0) {
        // 奇数番 左側に出力
        pdf.addImage(qrCode, format, Config.QrList.qr.rX, qrY, 22, 22)
      } else {
        // 偶数番 左側に出力
        pdf.addImage(qrCode, format, Config.QrList.qr.lX, qrY, 22, 22)
      }
    },
    // PDFにテキストをセット
    setText (pdf, count, floorCount, storeName, fullName) {
      let arr = []
      let twoLines = false

      // 店舗名の長さのチェック
      if (this.countStrLength(storeName) > Config.QrList.text.length) {
        twoLines = true

        for (let i = 14; i < storeName.length; i++) {
          // 1行の長さ分の文字列を取得
          if (this.countStrLength(storeName.substr(0, i)) >= Config.QrList.text.length) {
            if (arr.length === 1) {
              // 2行目
              arr.push(storeName.substr(0, (i - 1)))
              break
            }
            // 1行目
            arr.push(storeName.substr(0, i))
            storeName = storeName.substr(i)
          }
        }
      }
      // 2行目の文字列を格納
      if (twoLines && arr.length <= 1) arr.push(storeName)

      // 高さ調整
      let textY = 18 + (Config.QrList.lineHeight * floorCount)
      if ((count % 2) !== 0) {
        if (twoLines) {
          // 2行の場合: 偶数番 右側に出力
          pdf.text(arr[0], Config.QrList.text.rX, textY)
          if (arr.length === 2) pdf.text(arr[1], Config.QrList.text.rX, textY + 6)
          pdf.text(fullName, Config.QrList.text.rX, textY + Config.QrList.text.pad)
        } else {
          // 1行の場合: 偶数番 右側に出力
          pdf.text(storeName, Config.QrList.text.rX, textY)
          pdf.text(fullName, Config.QrList.text.rX, textY + Config.QrList.text.pad)
        }
      } else {
        if (twoLines) {
          // 2行の場合: 奇数番 左側に出力
          pdf.text(arr[0], Config.QrList.text.lX, textY)
          if (arr.length === 2) pdf.text(arr[1], Config.QrList.text.lX, textY + 6)
          pdf.text(fullName, Config.QrList.text.lX, textY + Config.QrList.text.pad)
        } else {
          // 1行の場合: 奇数番 左側に出力
          pdf.text(storeName, Config.QrList.text.lX, textY)
          pdf.text(fullName, Config.QrList.text.lX, textY + Config.QrList.text.pad)
        }
      }
    },
    // PDFの内容を設定
    createPdf (arrId) {
      // プログレスバー動作開始
      this.isProgress = true
      // 行数
      let lineCount = Math.floor(this.page / 2)
      // 従業員IDを取得
      let employeeId = arrId.shift()
      let img = document.getElementById(employeeId).firstElementChild.toDataURL()
      try {
        // QRコードを出力
        this.setQRImage(this.pdf, this.page, lineCount, img)

        let storeName = this.outputData[employeeId].storeName
        let fullName = this.outputData[employeeId].fullName
        // テキストを出力
        this.setText(this.pdf, this.page, lineCount, storeName, fullName)

        // 奇数番目の値が出力された場合は横線を引く
        if ((this.page % 2) === 0) {
          this.lineY += Config.QrList.lineHeight
          // 横線を入れる line(x1, y1, x2, y2, style)
          this.pdf.line(0, this.lineY, Config.QrList.line.X2, this.lineY)
        }

        // 10回になり、次の出力がある場合は次のページを用意
        if (this.page === 9 && arrId.length > 0) {
          // ページ切り替え前の最後の横線
          this.pdf.line(0, this.lineY, Config.QrList.line.X2, this.lineY)
          // 中央線を入れる
          this.drawACenterLine()
          // ページ追加
          this.pdf.addPage()
          // 横線を入れる line(x1, y1, x2, y2, style)
          this.pdf.line(0, 0, Config.QrList.line.X2, 0)
          // ページリセット
          this.page = 0
          // 横線の高さをリセット
          this.lineY = 0
        } else {
          this.page++
        }

        if (arrId.length > 0) {
          // 進捗を加算
          this.progressStatus += this.unit
          // callback
          this.createPdf(arrId)
        } else {
          // 中央線を入れる
          this.drawACenterLine()
          // 配列が最後ならpdf出力
          this.pdf.save('2D_code.pdf')
          // 各種変数をクリア
          this.resetVariable()
        }
      } catch (err) {
        // エラー発生
        console.log(err)
        // 各種変数をクリア
        this.resetVariable()
      }
    },
    // PDF出力処理
    outputPdf () {
      // チェックされているか判定
      if (this.checked.length === 0) return true

      // pdf初期化
      this.pdf = this.initPdf()
      // 値渡し
      let arrOutputId = this.checked.slice(0, this.checked.length)
      // 進捗の単位
      this.unit = Math.floor(100 / arrOutputId.length)

      // pdf出力するQR一覧のテキスト情報を整形
      this.checked.forEach(function (value) {
        for (let index = 0; index < this.employeeData.length; index++) {
          let valueId = String(value)
          let employeeId = String(this.employeeData[index].employeeID)

          if (valueId === employeeId) {
            let employee = this.employeeData[index]
            let text = []
            text['storeName'] = employee.storeName !== '' ? employee.storeName : ''
            text['fullName'] = employee.lastName + ' ' + employee.firstName
            // 従業員IDをキーにして値を格納
            this.outputData[valueId] = text
          }
        }
      }.bind(this))

      // PDF出力処理
      this.createPdf(arrOutputId)
    },
    // 各種変数のリセット
    resetVariable () {
      // ページリセット
      this.page = 0
      // 横線の高さをリセット
      this.lineY = 0
      // プログレスバー動作終了
      this.isProgress = false
      // 進捗リセット
      this.progressStatus = 0
      // pdfオブジェクトをクリア
      this.pdf = null
      // outputデータリセット
      this.outputData = []
    },
    // 全従業員処理
    allOutputPdf () {
      // 店舗名でフィルタ処理している場合はフィルタ条件に合致する従業員データを取得する
      // フィルタ処理をしていない場合は全件取得する
      let outputEmployeeDatas = this.filter(this.employeeData, this.search)

      // データがない場合は処理終了
      if (outputEmployeeDatas.length === 0) return true

      // QRコードのhtml取得のため一旦画面に全件表示
      let oldRows = this.pagination.rowsPerPage
      this.pagination.rowsPerPage = -1
      // 進捗の単位
      this.unit = Math.floor(100 / outputEmployeeDatas.length)
      // pdf初期化
      this.pdf = this.initPdf()

      // ID一覧を取得
      let arrOutputId = outputEmployeeDatas.map(employee => employee.employeeID)
      // pdf出力するQR一覧のテキスト情報を整形
      outputEmployeeDatas.forEach(employee => {
        let text = []
        text['storeName'] = employee.storeName !== '' ? employee.storeName : ''
        text['fullName'] = employee.lastName + ' ' + employee.firstName
        // 従業員IDをキーにして値を格納
        this.outputData[employee.employeeID] = text
      })
      // プログレスバー動作開始
      this.isProgress = true
      // PDF出力処理
      setTimeout(() => {
        this.createAllPdf(arrOutputId, oldRows)
      }, 1000)
    },
    // PDFの内容を設定
    createAllPdf (arrId, oldRows) {
      // 行数
      let lineCount = Math.floor(this.page / 2)
      // 従業員IDを取得
      let employeeId = arrId.shift()
      let img = document.getElementById(employeeId).firstElementChild.toDataURL()
      try {
        // QRコードを出力
        this.setQRImage(this.pdf, this.page, lineCount, img)

        let storeName = this.outputData[employeeId].storeName
        let fullName = this.outputData[employeeId].fullName
        // テキストを出力
        this.setText(this.pdf, this.page, lineCount, storeName, fullName)

        // 奇数番目の値が出力された場合は横線を引く
        if ((this.page % 2) === 0) {
          this.lineY += Config.QrList.lineHeight
          // 横線を入れる line(x1, y1, x2, y2, style)
          this.pdf.line(0, this.lineY, Config.QrList.line.X2, this.lineY)
        }

        // 10回になり、次の出力がある場合は次のページを用意
        if (this.page === 9 && arrId.length > 0) {
        // ページ切り替え前の最後の横線
          this.pdf.line(0, this.lineY, Config.QrList.line.X2, this.lineY)
          // 中央線を入れる
          this.drawACenterLine()
          // ページ追加
          this.pdf.addPage()
          // 横線を入れる line(x1, y1, x2, y2, style)
          this.pdf.line(0, 0, Config.QrList.line.X2, 0)
          // ページリセット
          this.page = 0
          // 横線の高さをリセット
          this.lineY = 0
        } else {
          this.page++
        }

        if (arrId.length > 0) {
        // 進捗を加算
          this.progressStatus += this.unit

          // callback
          this.createAllPdf(arrId, oldRows)
        } else {
        // 中央線を入れる
          this.drawACenterLine()
          // 配列が最後ならpdf出力
          this.pdf.save('2D_code.pdf')
          // 各種変数をクリア
          this.resetVariable()
          this.pagination.rowsPerPage = oldRows
        }
      } catch (err) {
      // エラー発生
        console.log(err)
        // 各種変数をクリア
        this.resetVariable()

        // this.pagination.rowsPerPage = oldRows
      }
    },
    // ソート機能
    changeSort (column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
        // チェックをクリア
        // this.checked = []
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
        // チェックをクリア
        // this.checked = []
      }
    },
    // データベース受け取った従業員データの格納
    setEmployeeData () {
      let self = this
      // データベースから取得した従業員一覧をemployeeDatに格納する関数
      function callbackSetDbEmployeeData (dbEmployeeData) {
        if (dbEmployeeData !== undefined) {
          // データ存在時
          for (let i = 0; i < dbEmployeeData.length; i++) {
            if (dbEmployeeData[i].employment_period_end) {
              // 雇用期間終了日が設定されている場合

              let momentDbEnd = moment(dbEmployeeData[i].employment_period_end).format('YYYY-MM-DD')
              let momentToday = moment().format('YYYY-MM-DD')
              // 雇用期間終了日が現在日付より前の日の場合は表示しない
              if (momentDbEnd < momentToday) continue
            }
            // 主店舗のみ表示する。
            if (dbEmployeeData[i].haccpresnumber__c === dbEmployeeData[i].hc_haccpresnumber__c) {
              self.employeeData.push({
                // 事業者ID
                businessCompanyId: dbEmployeeData[i].haccpcomnumber__c,
                // 従業員ID
                employeeID: dbEmployeeData[i].id,
                // 店舗ID
                storeID: dbEmployeeData[i].haccpresnumber__c,
                // 店舗名
                storeName: dbEmployeeData[i].name,
                // 名
                firstName: dbEmployeeData[i].first_name,
                // 姓
                lastName: dbEmployeeData[i].last_name,
                // 検索用フルネーム
                fullName: dbEmployeeData[i].last_name + dbEmployeeData[i].first_name,
                // チェック状態
                checked: false
              })
            }
          }
        }
        self.isLoading = false
        // プログレスバーの非表示
        self.$store.dispatch('setIsLoading', false)
      }

      let data = {}
      data.callback = callbackSetDbEmployeeData
      // 従業員一覧の取得
      this.$store.dispatch('managementFunction/actionSelectEmployeeData', data)
    },
    // フィルター処理
    filter (datas, search) {
      return SystemAndManagement.filterMain(datas, search, ['businessCompanyId', 'storeID', 'checked'])
    },
    // 半角は1文字, 全角は2文字として文字数をカウントする
    countStrLength (str) {
      let cnt = 0
      let c = ''
      for (let i = 0, len = str.length; i < len; i++) {
        c = str.charCodeAt(i)
        if ((c >= 0x0 && c < 0x81) || (c === 0xa0) || (c >= 0xa1 && c < 0xdf) || (c >= 0xfd && c < 0xff)) {
          // 半角
          cnt += 1
        } else {
          // 全角
          cnt += 2
        }
      }
      return cnt
    }
  }
}
</script>
